import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import gsap from 'gsap/all';
import { useTranslation } from 'react-i18next';

import { useAudioContext } from '../../context/AudioContext';
import { useDetailViewContext } from '../../context/DetailViewContext';
import { useNavigationContext } from '../../context/NavigationContext';

import { ConclusionPages, DetailSectionIDs, IntroductionPages, SectionIDs } from '../../data/Sections';
import FocusPoints from '../../data/FocusPoints';

import useWindowSize from '../../hooks/useWindowSize';

import { ReactComponent as MenuCloseIcon} from '../../assets/images/icons/menu-close.svg';
import { ReactComponent as ArrowRightIcon } from '../../assets/images/icons/arrow-right.svg';
import { ReactComponent as ChevronDownIcon } from '../../assets/images/icons/down-arrow.svg';
import { ReactComponent as YoutubeIcon } from '../../assets/images/icons/youtube.svg';
import { ReactComponent as TwitterIcon } from '../../assets/images/icons/twitter.svg';
import { ReactComponent as LinkedinIcon } from '../../assets/images/icons/linkedin.svg';
import { ReactComponent as ResearchGateIcon } from '../../assets/images/icons/research-gate.svg';

import styles from './styles.module.scss';

interface IMenu {
  hideMenu: () => void;
  video?: HTMLVideoElement
}

function Menu({ hideMenu, video }: IMenu): JSX.Element {
  const { t, i18n } = useTranslation('ai4Jokes');
  const { playAudio } = useAudioContext();
  const { activeSectionID, moveToTheSection } = useNavigationContext();
  const { activeDetailID, setDetailSectionID, setActiveFocusID } = useDetailViewContext();
  const {width} = useWindowSize();

  const subMenuRef = useRef<HTMLDivElement>(null);
  const subMenuArrowRef = useRef<HTMLImageElement>(null);
  const [isSubMenuOpen, setIsSubMenuOpen] = useState(false);

  const gotoSection = useCallback(
    (sectionId: SectionIDs) => {
      playAudio('audioCTA');
      hideMenu();
      moveToTheSection(sectionId);
    },
    [playAudio, hideMenu, moveToTheSection],
  );

  const gotoIntroduction = useCallback(
    () => {
      gotoSection(SectionIDs.Intro1);
    },
    [gotoSection],
  );

  const gotoTheJoke = useCallback(
    () => {
      gotoSection(SectionIDs.Joke);
      video?.play();
    },
    [gotoSection],
  );

  const gotoDecodingVector = useCallback(
    () => {
      gotoSection(SectionIDs.Science);
      setActiveFocusID(FocusPoints.None);
      setDetailSectionID(DetailSectionIDs.DecodingVector);
    },
    [gotoSection, setDetailSectionID, setActiveFocusID],
  );
  const gotoDecodingSphere = useCallback(
    () => {
      gotoSection(SectionIDs.Science);
      setActiveFocusID(FocusPoints.None);
      setDetailSectionID(DetailSectionIDs.DecodingSphere);
    },
    [gotoSection, setDetailSectionID, setActiveFocusID],
  );
  const gotoDecodingSubspaces = useCallback(
    () => {
      gotoSection(SectionIDs.Science);
      setActiveFocusID(FocusPoints.None);
      setDetailSectionID(DetailSectionIDs.DecodingSubspaces);
    },
    [gotoSection, setDetailSectionID, setActiveFocusID],
  );
  const gotoDecodingTopology = useCallback(
    () => {
      gotoSection(SectionIDs.Science);
      setActiveFocusID(FocusPoints.None);
      setDetailSectionID(DetailSectionIDs.DecodingTopology);
    },
    [gotoSection, setDetailSectionID, setActiveFocusID],
  );
  const gotoDecodingDynamicBehaviour = useCallback(
    () => {
      gotoSection(SectionIDs.Science);
      setActiveFocusID(FocusPoints.None);
      setDetailSectionID(DetailSectionIDs.DecodingDynamicBehaviour);
    },
    [gotoSection, setDetailSectionID, setActiveFocusID],
  );

  const gotoConclusion = useCallback(
    () => {
      gotoSection(SectionIDs.Conclusion);
    },
    [gotoSection],
  );

  const isIntroductionActive = useMemo(() => IntroductionPages.includes(activeSectionID), [activeSectionID]);
  const isConclusionActive = useMemo(() => ConclusionPages.includes(activeSectionID), [activeSectionID]);

  const isDecodingVectorActive = useMemo(() => activeDetailID === DetailSectionIDs.DecodingVector, [activeDetailID]);
  const isDecodingSphereActive = useMemo(() => activeDetailID === DetailSectionIDs.DecodingSphere, [activeDetailID]);
  const isDecodingSubspacesActive = useMemo(() => activeDetailID === DetailSectionIDs.DecodingSubspaces, [activeDetailID]);
  const isDecodingTopologyActive = useMemo(() => activeDetailID === DetailSectionIDs.DecodingTopology, [activeDetailID]);
  const isDecodingDynamicBehaviourActive = useMemo(() => activeDetailID === DetailSectionIDs.DecodingDynamicBehaviour, [activeDetailID]);

  const isSubMenuSelected = useMemo(() => isDecodingVectorActive || isDecodingSphereActive || isDecodingSubspacesActive || isDecodingTopologyActive || isDecodingDynamicBehaviourActive,
    [isDecodingVectorActive, isDecodingSphereActive, isDecodingSubspacesActive, isDecodingTopologyActive, isDecodingDynamicBehaviourActive]);

  const toggleSubMenu = useCallback(
    () => {
      setIsSubMenuOpen(isSubMenuOpen => !isSubMenuOpen);
    }, []);

  useEffect(() => {
    if(isSubMenuOpen || isSubMenuSelected){
      gsap.to(subMenuRef.current, {height: width > 600 ? 'auto' : 'auto', marginTop: '2.68rem', duration: 0.4});
      gsap.to(subMenuArrowRef.current, {transform: 'rotate(0deg)', duration: 0.1});
    }else{
      gsap.to(subMenuRef.current, {height: '0rem', marginTop: '0', duration: 0.4});
      gsap.to(subMenuArrowRef.current, {transform: 'rotate(-90deg)', duration: 0.1});
    }
  }, [isSubMenuOpen, isSubMenuSelected, width]);

  // prevent clicks to get through to the 3D view, causing advanced modes to be triggered by clicking on the overlay
  const stopPropagation = useCallback(event => {
    event.nativeEvent.stopImmediatePropagation();
  }, []);

  return <div
    className={styles.container}
    onClick={e => stopPropagation(e)}
  >
    <div className={styles.menuHeader}>
      <button
        className={styles.headerLogo}
        onClick={() => {
          gotoSection(SectionIDs.Title);
        }}
      >
        <picture >
          <source srcSet={require('../../assets/images/logo/logo-inverted.webp').default} type='image/webp' />
          <img src={require('../../assets/images/logo/logo-inverted.png').default} alt='logo' />
        </picture>
      </button>

      <button
        title='close menu'
        className={styles.closeButton}
        onClick={() => {
          hideMenu();
          playAudio('audioMenuOpenClose');
        }}>
        <MenuCloseIcon />
      </button>
    </div>
    <div className={styles.innerContainer}>
      <section className={styles.menuLinkList}>
        <button
          onClick={gotoIntroduction}
          data-selected={isIntroductionActive}
          className={styles.menuLink}
          data-headline={'true'}
        >
          <span data-title='true'>{t('menu.intro.line-1')}:&nbsp;</span>
          <span>{t('menu.intro.line-2')}</span>
        </button>
        <button
          onClick={gotoTheJoke}
          data-selected={activeSectionID === SectionIDs.Joke}
          className={styles.menuLink}
        >
          <span data-title='true'>{t('menu.joke')}</span>
        </button>
        <div>
          <button
            onClick={toggleSubMenu}
            data-selected={activeSectionID === SectionIDs.Science}
            className={styles.menuLink}
            data-headline={'true'}
          >
            <ChevronDownIcon data-open={isSubMenuOpen} data-main-icon />
            <span data-title='true'>{t('menu.science.line-1')}:&nbsp;</span>
            <span>{t('menu.science.line-2')}</span>
          </button>
          <div ref={subMenuRef} className={styles.expandableWrapper}>
            <div className={styles.expandableContent}>
              <button
                onClick={gotoDecodingVector}
                data-selected={isDecodingVectorActive}
                className={[styles.menuLink, styles.subMenuLink].join(' ')}
              >
                <span className={[styles.menuLinkBold].join(' ')}>{t('vectorrepresentation.title_1')}
                  <span className={[styles.menuLinkColon].join(' ')}>:&nbsp;</span>
                </span>
                <span className={[styles.menuLinkSubtitle].join(' ')}>{t('vectorrepresentation.title_2')}</span>
                {isDecodingVectorActive && <ArrowRightIcon />}
              </button>
              <button
                onClick={gotoDecodingSphere}
                data-selected={isDecodingSphereActive}
                className={[styles.menuLink, styles.subMenuLink].join(' ')}
              >
                <span className={[styles.menuLinkBold].join(' ')}>{t('sphereembedding.title_1')}
                  <span className={[styles.menuLinkColon].join(' ')}>:&nbsp;</span>
                </span>
                <span className={[styles.menuLinkSubtitle].join(' ')}>{t('sphereembedding.title_2')}</span>
                {isDecodingSphereActive && <ArrowRightIcon />}
              </button>
              <button
                onClick={gotoDecodingSubspaces}
                data-selected={isDecodingSubspacesActive}
                className={[styles.menuLink, styles.subMenuLink].join(' ')}
              >
                <span className={[styles.menuLinkBold].join(' ')}>{t('spheresubspaces.title_1')}
                  <span className={[styles.menuLinkColon].join(' ')}>:&nbsp;</span>
                </span>
                <span className={[styles.menuLinkSubtitle].join(' ')}>{t('spheresubspaces.title_2')}</span>
                {isDecodingSubspacesActive && <ArrowRightIcon />}
              </button>
              <button
                onClick={gotoDecodingTopology}
                data-selected={isDecodingTopologyActive}
                className={[styles.menuLink, styles.subMenuLink].join(' ')}
              >
                <span className={[styles.menuLinkBold].join(' ')}>{t('topologicalrelations.title_1')}
                  <span className={[styles.menuLinkColon].join(' ')}>:&nbsp;</span>
                </span>
                <span className={[styles.menuLinkSubtitle].join(' ')}>{t('topologicalrelations.title_2')}</span>
                {isDecodingTopologyActive && <ArrowRightIcon />}
              </button>
              <button
                onClick={gotoDecodingDynamicBehaviour}
                data-selected={isDecodingDynamicBehaviourActive}
                className={[styles.menuLink, styles.subMenuLink].join(' ')}
              >
                <span className={[styles.menuLinkBold].join(' ')}>{t('dynamicbehavior.title_1')}
                  <span className={[styles.menuLinkColon].join(' ')}>:&nbsp;</span>
                </span>
                <span className={[styles.menuLinkSubtitle].join(' ')}>{t('dynamicbehavior.title_2')}</span>
                {isDecodingDynamicBehaviourActive && <ArrowRightIcon />}
              </button>
            </div>
          </div>
        </div>
        <button
          onClick={gotoConclusion} 
          data-selected={isConclusionActive}
          className={styles.menuLink}
        >
          <span data-title='true'>{t('menu.conclusion')}</span>
        </button>
      </section>
    </div>
    <footer className={styles.footer}>
      <div className={styles.linksTop}>
        <a href='https://machinelearning-blog.de/' target='_blank' rel='noreferrer'>{t('menu.blog')}</a>
        <a href={t('menu.privacy-url')} target='_blank' rel='noreferrer'>{t('menu.privacy')}</a>
        <a href={t('menu.imprint-url')} target='_blank' rel='noreferrer'>{t('menu.imprint')}</a>
      </div>
      <div className={styles.language}>
        <button
          data-active={i18n.language === 'en'}
          onClick={() => i18n.changeLanguage('en')}
        >EN</button>
        <hr aria-hidden={true} />
        <button
          data-active={i18n.language === 'de'}
          onClick={() => i18n.changeLanguage('de')}
        >DE</button>
      </div>
      <div className={styles.socialMedia}>
        <a
          href='https://www.youtube.com/channel/UCdQDnS-ARz_JUEMeeCxMiDw' 
          target='_blank'
          rel='noreferrer'
        >
          <YoutubeIcon />
        </a>
        <a
          href='https://twitter.com/LamarrInstitute '
          target='_blank'
          rel='noreferrer'
        >
          <TwitterIcon />
        </a>
        <a
          href='https://www.linkedin.com/company/lamarr-institut/ '
          target='_blank'
          rel='noreferrer'
        >
          <LinkedinIcon />
        </a>
        <a
          href='https://www.researchgate.net/project/Lamarr-Institute-for-Machine-Learning-and-Artificial-Intelligence'
          target='_blank'
          rel='noreferrer'
        >
          <ResearchGateIcon />
        </a>
      </div>
      <picture className={styles.footerLogo}>
        <source srcSet={require('../../assets/images/logo/logo-inverted.webp').default} type='image/webp' />
        <img src={require('../../assets/images/logo/logo-inverted.png').default} alt='logo' />
      </picture>
      <small className={styles.copyright}>© ML2R – Kompetenzzentrum Maschinelles Lernen Rhein-Ruhr 2022</small>
    </footer>
  </div>;
}

export default React.memo(Menu);